import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Chip, Tooltip, Typography } from '@heroui/react';
import { ListView, NumberStepper } from '@heroui-pro/react';
import { Icon } from '@iconify/react';
import { getNodeTestIdProp } from '@/utils';
import {
    LINE_DISCOUNT_SHORTCUT,
    renderWithKeyShortcuts,
    ShortcutHint,
} from '@/components/shortcuts';
import { formatDiscountValue, formatGel, variantLabel } from '../../sellerMeta';
import type { CartLineView } from './useCartPanel';

const renderDiscountButton = renderWithKeyShortcuts(
    'button',
    LINE_DISCOUNT_SHORTCUT.combination,
);

type CartItemRowProps = {
    view: CartLineView;
    onQnt: (qnt: number) => void;
    onRemove: () => void;
    /** Opens the discount dialog for this line, the same one `F6` opens. */
    onDiscount: () => void;
    /** Captures the row's DOM node for `F4` and post-`Delete` refocus. See `useCartKeyboardNav`. */
    itemRef: (node: HTMLElement | null) => void;
};

/**
 * One row of the cart's `ListView` grid list (bos-seller#56), which is one
 * variant, in two rows (SEL-100). First: the product name (and the variant's
 * label when the product has several) and the line total, with the list
 * total struck through beside it when the line is discounted (unknown, `—`,
 * while its discount no longer fits). Second: the
 * unit price, the discount as entered in a tag (danger when it no longer
 * fits the line) and a note mark, then the quantity stepper (bounded by the
 * variant's sellable quantity), the discount button and the remove button.
 * The `F6` keycap shows beside the discount button only while the line holds
 * focus, since `F6` acts on the focused line.
 *
 * The stepper and both buttons are named after the line, so two variants of
 * one product stay distinguishable to a screen reader, and both buttons are
 * out of the Tab order: the row is one Tab stop, and `→`/`←` walk into them.
 * `data-cart-line` names the row's variant for `useCartKeyboardNav`, which
 * reads it from whatever inside the row has focus — the stepper's visually
 * hidden field included. Render-only — figures and callbacks come from the
 * cart panel. Numeric values use tabular figures so they align down the
 * column.
 */
export const CartItemRow: React.FC<CartItemRowProps> = ({
    view,
    onQnt,
    onRemove,
    onDiscount,
    itemRef,
}) => {
    const { t } = useTranslation('pages');
    const { line, listTotal, total, isDiscountInvalid } = view;
    const { product, variant, qnt, discount } = line;
    const label = variantLabel(product, variant);
    const lineName =
        label === null
            ? product.name
            : t('seller.cartLineVariant', {
                  product: product.name,
                  variant: label,
              });
    const id = variant.variantId;

    return (
        <ListView.Item
            className="group"
            {...getNodeTestIdProp(`seller-cart-item-${id}`)}
            data-cart-line={id}
            id={id}
            ref={itemRef}
            textValue={lineName}
        >
            <ListView.ItemContent>
                <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                    <div className="min-w-0">
                        <Typography truncate type="body-sm">
                            {product.name}
                        </Typography>
                        {label === null ? null : (
                            <Typography truncate type="body-xs">
                                {label}
                            </Typography>
                        )}
                    </div>
                    <div className="flex min-h-7 flex-wrap items-center gap-x-2 gap-y-1">
                        <Typography
                            className="shrink-0 whitespace-nowrap tabular-nums"
                            color="muted"
                            type="body-xs"
                        >
                            {formatGel(variant.price)}
                        </Typography>
                        {discount ? (
                            <Chip
                                className="shrink-0 whitespace-nowrap"
                                // A discount that no longer fits gets a neutral tag (the soft
                                // danger Chip is 3.22:1 in every theme); the notice and the
                                // unknown figures say what is wrong.
                                color={
                                    isDiscountInvalid ? 'default' : 'warning'
                                }
                                {...getNodeTestIdProp(
                                    `seller-cart-discount-${id}`,
                                )}
                                size="sm"
                                variant={isDiscountInvalid ? 'soft' : 'primary'}
                            >
                                {`−${formatDiscountValue(discount)}`}
                            </Chip>
                        ) : null}
                        {discount && discount.note !== '' ? (
                            <span
                                aria-label={t('seller.cartDiscount.note', {
                                    note: discount.note,
                                })}
                                className="flex text-muted"
                                {...getNodeTestIdProp(`seller-cart-note-${id}`)}
                                role="img"
                                title={discount.note}
                            >
                                <Icon
                                    className="size-3.5"
                                    icon="solar:notes-linear"
                                />
                            </span>
                        ) : null}
                    </div>
                </div>
            </ListView.ItemContent>
            <ListView.ItemAction>
                <div className="flex flex-col items-end gap-1.5">
                    <div className="flex items-baseline gap-2 tabular-nums">
                        {discount ? (
                            <Typography
                                className="line-through"
                                color="muted"
                                {...getNodeTestIdProp(
                                    `seller-cart-list-total-${id}`,
                                )}
                                type="body-xs"
                            >
                                {formatGel(listTotal)}
                            </Typography>
                        ) : null}
                        <Typography
                            {...getNodeTestIdProp(`seller-cart-total-${id}`)}
                            type="body-sm"
                            weight="semibold"
                        >
                            {isDiscountInvalid
                                ? t('seller.discount.unknown')
                                : formatGel(discount ? total : listTotal)}
                        </Typography>
                    </div>
                    <div className="flex items-center gap-1">
                        <NumberStepper
                            aria-label={lineName}
                            maxValue={variant.sellableQuantity}
                            minValue={1}
                            onChange={onQnt}
                            size="sm"
                            value={qnt}
                        >
                            <NumberStepper.Group>
                                <NumberStepper.DecrementButton />
                                <NumberStepper.Value />
                                <NumberStepper.IncrementButton />
                            </NumberStepper.Group>
                        </NumberStepper>
                        <span className="hidden group-focus-within:inline-flex">
                            <ShortcutHint shortcut={LINE_DISCOUNT_SHORTCUT} />
                        </span>
                        <Button
                            aria-label={t(
                                discount
                                    ? 'seller.cartDiscount.editLine'
                                    : 'seller.cartDiscount.addLine',
                                { name: lineName },
                            )}
                            {...getNodeTestIdProp(
                                `seller-cart-discount-button-${id}`,
                            )}
                            excludeFromTabOrder
                            isIconOnly
                            onPress={onDiscount}
                            render={renderDiscountButton}
                            size="sm"
                            variant="ghost"
                        >
                            <Icon
                                className={
                                    discount
                                        ? 'size-4 text-warning-soft-foreground'
                                        : 'size-4 text-muted'
                                }
                                icon="solar:tag-price-linear"
                            />
                        </Button>
                        <Tooltip delay={0}>
                            <Button
                                aria-label={t('seller.removeLine', {
                                    name: lineName,
                                })}
                                {...getNodeTestIdProp(
                                    `seller-cart-remove-${id}`,
                                )}
                                // Out of the Tab order, like the stepper's own
                                // buttons — the row is one Tab stop; the arrow-key
                                // walker still reaches this button by `→` since
                                // it isn't filtered to only tabbable elements
                                // (bos-seller#56, the spike result on #47).
                                excludeFromTabOrder
                                isIconOnly
                                onPress={onRemove}
                                size="sm"
                                variant="ghost"
                            >
                                <Icon
                                    className="size-4 text-danger"
                                    icon="solar:trash-bin-trash-linear"
                                />
                            </Button>
                            <Tooltip.Content>
                                {t('seller.remove')}
                            </Tooltip.Content>
                        </Tooltip>
                    </div>
                </div>
            </ListView.ItemAction>
        </ListView.Item>
    );
};
